import React, { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Pretendard } from '@/styles/fonts';
import { isIOS, isMobile, isNativeApp } from '@/utils/userEnv';
import { getCloudflareResizedImageUrl } from '@/utils/function';
import { appDarkRichContentSx } from '@/styles/appDarkMode';

export type BoardDetailContentProps = {
  content: string;
  searchText?: string;
  focusedResultId?: string; // 포커싱할 검색 결과 ID
  hoveredResultId?: string | null; // hover된 검색 결과 ID
  scrollTrigger?: number; // 스크롤 강제 트리거
};

const getCloudflareResizedUrl = (originalUrl: string) => {
  // 본문 이미지는 모바일 기기일 때만 리사이즈한다(PC 는 원본 서빙).
  if (!isMobile()) return originalUrl;

  return getCloudflareResizedImageUrl(originalUrl);
};

const processHtmlContent = (text: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');

  // 1. iframe을 버튼으로 대체 (기존 로직)
  // iOS 포털 앱에서 영상 첨부된 게시글 조회 시 리다이렉트 되는 문제 있음
  // https://fnf.atlassian.net/browse/PORTAL-5022
  // iframe 태그를 버튼 태그로 변경하고, 클릭 시 새탭으로 영상 재생
  if (isNativeApp() && isIOS(window.navigator.userAgent)) {
    const iframes = doc.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      const iframeSrc = iframe.getAttribute('src') || '';

      // https://file.fnf.co.kr/social-vibecode로 시작하는 경우 대체하지 않음
      if (iframeSrc.startsWith('https://file.fnf.co.kr/social-vibecode')) {
        return;
      }

      if (iframe.parentElement) {
        iframe.parentElement.style.padding = '0px';
        iframe.parentElement.style.height = 'auto';
      }
      const button = document.createElement('button');
      button.classList.add('iframe-link');
      button.textContent = '👉 동영상 재생';
      button.setAttribute('data-src', iframeSrc);
      iframe.replaceWith(button);
    });
  }

  // 2. 이미지 URL 변환 및 에러 핸들링 (Cloudflare 적용)
  const images = doc.querySelectorAll('img');
  images.forEach((img) => {
    const currentSrc = img.getAttribute('src');

    if (currentSrc && currentSrc.includes('fnf.co.kr')) {
      const resizedSrc = getCloudflareResizedUrl(currentSrc);

      // 리사이징 주소가 원본과 다를 경우에만 적용
      if (resizedSrc !== currentSrc) {
        img.setAttribute('src', resizedSrc);
        img.setAttribute(
          'onerror',
          `this.onerror=null; this.src='${currentSrc}';`,
        );
      }

      // 성능을 위한 Lazy Loading
      img.setAttribute('loading', 'lazy');
    }
  });

  return doc.body.innerHTML;
};

/**
 * HTML 문자열에서 searchText를 찾아서 하이라이트 처리된 HTML 반환
 */
const highlightSearchTextInHTML = (
  htmlString: string,
  searchText: string,
): string => {
  if (!searchText || searchText.trim() === '') {
    return htmlString;
  }

  // DOM 파싱
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const lowerSearchText = searchText.toLowerCase();
  let matchIndex = 0; // 각 하이라이트에 고유 ID 부여

  // 재귀적으로 모든 텍스트 노드를 찾아서 처리
  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();

      if (!lowerText.includes(lowerSearchText)) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let index = lowerText.indexOf(lowerSearchText, lastIndex);

      while (index !== -1) {
        // 매칭 전 텍스트
        if (index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex, index)),
          );
        }

        // 매칭된 텍스트 (하이라이트)
        const mark = document.createElement('mark');
        mark.id = `search-result-${matchIndex}`; // 고유 ID 부여
        mark.className = 'search-highlight';
        mark.style.backgroundColor = '#FFECC2'; // var(--status-hold-24)
        mark.style.color = '#000';
        mark.style.borderRadius = '4px';
        mark.style.padding = '0 3px';
        mark.style.lineHeight = '1.5';
        mark.style.border = '3px solid transparent'; // 기본은 투명
        mark.style.transition = 'all 0.3s ease';
        mark.textContent = text.slice(index, index + searchText.length);
        fragment.appendChild(mark);

        matchIndex++;
        lastIndex = index + searchText.length;
        index = lowerText.indexOf(lowerSearchText, lastIndex);
      }

      // 남은 텍스트
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      node.parentNode?.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const elem = node as Element;

      // mark 태그는 스킵 (이미 하이라이트된 것)
      if (elem.tagName === 'MARK') {
        return;
      }

      // 자식 노드들을 배열로 복사 후 역순으로 처리
      const children = Array.from(node.childNodes);
      for (let i = children.length - 1; i >= 0; i--) {
        processNode(children[i]);
      }
    }
  };

  processNode(doc.body);
  return doc.body.innerHTML;
};

const replaceContent = (text: string) => {
  let result = text
    .replace(/href/g, 'target="_blank" href')
    .replace(/100%px/g, '100%');

  // DOM 요소를 직접 조작해야 하는 복합 로직(iframe, img) 처리
  result = processHtmlContent(result);

  return result;
};

const BoardDetailContent = ({
  searchText,
  content,
  focusedResultId,
  hoveredResultId,
  scrollTrigger,
}: BoardDetailContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousContentRef = useRef<string>('');
  const previousSearchTextRef = useRef<string>('');
  const previousFocusedIdRef = useRef<string>('');
  const previousHoveredIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const contentChanged = previousContentRef.current !== content;
    const searchTextChanged = previousSearchTextRef.current !== searchText;

    // content가 변경되었거나 searchText가 변경되었을 때
    if (contentChanged || searchTextChanged) {
      // 1. 원본 content를 replaceContent로 처리
      const processedContent = replaceContent(content);

      // 2. searchText가 있으면 하이라이트 적용 후 innerHTML 설정
      containerRef.current.innerHTML =
        searchText && searchText.trim() !== ''
          ? highlightSearchTextInHTML(processedContent, searchText)
          : processedContent;

      // 4. iOS 네이티브 앱에서 버튼 이벤트 바인딩
      if (isNativeApp() && isIOS(window.navigator.userAgent)) {
        const buttons: NodeListOf<HTMLButtonElement> =
          containerRef.current.querySelectorAll('button.iframe-link');

        buttons.forEach((button) => {
          const src = button.getAttribute('data-src') || '';
          button.onclick = () => {
            window.open(src, '_blank');
          };
        });
      }

      // 5. 이전 값 업데이트
      previousContentRef.current = content;
      previousSearchTextRef.current = searchText || '';
    }
  }, [content, searchText]);

  // 포커싱된 결과로 스크롤 및 포커싱 스타일 적용
  useLayoutEffect(() => {
    if (!containerRef.current || !focusedResultId) {
      return;
    }

    // 하이라이트가 렌더링될 때까지 잠시 대기
    let retryCount = 0;
    const maxRetries = 10;

    const scrollToTarget = () => {
      if (!containerRef.current) return;

      // 이전에 포커싱된 요소의 스타일 제거 (기본 스타일로)
      if (previousFocusedIdRef.current) {
        const prevElement = containerRef.current.querySelector(
          `#${previousFocusedIdRef.current}`,
        );
        if (prevElement instanceof HTMLElement) {
          prevElement.style.border = '3px solid transparent';
          prevElement.style.backgroundColor = '#FFECC2';
        }
      }

      // 새로운 요소로 스크롤 및 포커싱 스타일 적용
      const targetElement = containerRef.current.querySelector(
        `#${focusedResultId}`,
      );

      if (targetElement instanceof HTMLElement) {
        // 포커싱 스타일 적용 (hover와 동일)
        targetElement.style.border = '3px solid #FF9A15';
        targetElement.style.backgroundColor = '#FFECC2';

        // 스크롤
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        previousFocusedIdRef.current = focusedResultId;
      } else {
        // 요소가 아직 없으면 다음 프레임에 재시도
        retryCount++;
        if (retryCount < maxRetries) {
          requestAnimationFrame(scrollToTarget);
        }
      }
    };

    // 다음 프레임에 실행 (하이라이트 렌더링 후)
    requestAnimationFrame(scrollToTarget);
  }, [focusedResultId, scrollTrigger]);

  // hover된 결과에 스타일 적용
  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const hoveredIdChanged = previousHoveredIdRef.current !== hoveredResultId;
    if (!hoveredIdChanged) return;

    // 이전에 hover된 요소의 스타일 제거 (기본 스타일로)
    if (previousHoveredIdRef.current) {
      const prevElement = containerRef.current.querySelector(
        `#${previousHoveredIdRef.current}`,
      );
      if (prevElement instanceof HTMLElement) {
        prevElement.style.border = '3px solid transparent';
        prevElement.style.backgroundColor = '#FFECC2';
      }
    }

    // 새로운 hover 요소에 hover 스타일 적용
    if (hoveredResultId) {
      const targetElement = containerRef.current.querySelector(
        `#${hoveredResultId}`,
      );

      if (targetElement instanceof HTMLElement) {
        targetElement.style.border = '3px solid #FF9A15'; // var(--Status-Warning)
        targetElement.style.backgroundColor = '#FFECC2'; // var(--status-hold-24)
      }
    }

    previousHoveredIdRef.current = hoveredResultId ?? null;
  }, [hoveredResultId]);

  return (
    <Box
      ref={containerRef}
      component={'section'}
      className={Pretendard.className}
      sx={{
        paddingTop: '20px',
        // 앱 다크 모드: 작성자가 넣은 검정 글자 · 흰 배경만 보정
        ...appDarkRichContentSx,
        '*': { maxWidth: '100% !important' },
        // 이미지 사이즈를 줄였을 때 대응
        'img[style*="width: inherit"], img[style*="height: inherit"]': {
          width: 'auto !important',
          height: 'auto !important',
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
        },
        'ol:not(.PlaygroundEditorTheme__ol1):not(.PlaygroundEditorTheme__ol2):not(.PlaygroundEditorTheme__ol3):not(.PlaygroundEditorTheme__ol4):not(.PlaygroundEditorTheme__ol5)':
          {
            listStyle: 'none',
            margin: 0,
            padding: 0,
          },
        'ul:not(.PlaygroundEditorTheme__ul)': {
          listStyle: 'none',
          margin: 0,
          padding: 0,
        },
        'button.iframe-link': {
          display: 'block',
          width: 'fit-content',
          padding: '10px 12px',
          border: '1px solid black',
          borderRadius: '6px',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'var(--dk-accent-soft, #EEF4FC)' },
        },
        // iframe 리렌더링 방지를 위한 최적화
        iframe: {
          pointerEvents: 'auto',
          willChange: 'auto',
        },
      }}
    />
  );
};

export default BoardDetailContent;
